import { createSlice } from "@reduxjs/toolkit";
import {
  getAccounts,
  getAccount,
  addNewAccount,
  updateAccount,
  deleteAccount,
  getAccountsByPhase,
} from "./thunk";

export const initialState = {
  accounts: [],
  account: null,
  error: null,
  isAccountSuccess: false,
  isAccountCreated: false,
  isAccountFetched: false,
  isLoading: false,
};

const accountsSlice = createSlice({
  name: "AccountsSlice",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    
    clearAccount: (state) => {
      state.account = null;
      state.isAccountFetched = false;
    },
    
    resetAccountFlags: (state) => {
      state.isAccountCreated = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch accounts
    builder
      .addCase(getAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAccounts.fulfilled, (state, action) => {
        state.accounts = action.payload;
        state.isAccountSuccess = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getAccounts.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch accounts";
        state.isAccountSuccess = false;
        state.isLoading = false;
      });

    // Fetch single account
    builder
      .addCase(getAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAccount.fulfilled, (state, action) => {
        state.account = action.payload;
        state.isAccountFetched = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getAccount.rejected, (state, action) => {
        state.account = null;
        state.isAccountFetched = false;
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch account details";
      });

    // Add new account
    builder
      .addCase(addNewAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isAccountCreated = false;
      })
      .addCase(addNewAccount.fulfilled, (state, action) => {
        state.accounts.push(action.payload.data);
        state.isAccountCreated = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(addNewAccount.rejected, (state, action) => {
        state.error = action.payload || "Failed to add account";
        state.isAccountCreated = false;
        state.isLoading = false;
      });

    // Update account
    builder
      .addCase(updateAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAccount.fulfilled, (state, action) => {
        state.accounts = state.accounts.map((account) =>
          account.id === action.payload.id ? { ...account, ...action.payload } : account
        );
        if (state.account && state.account.id === action.payload.id) {
          state.account = { ...state.account, ...action.payload };
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.error = action.payload || "Failed to update account";
        state.isLoading = false;
      });

    // Delete account
    builder
      .addCase(deleteAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.accounts = state.accounts.filter(
          (account) => account.id !== action.payload
        );
        if (state.account && state.account.id === action.payload) {
          state.account = null;
          state.isAccountFetched = false;
        }
        state.isLoading = false;
        state.error = null;
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete account";
        state.isLoading = false;
      });

    // Get accounts by phase
    builder
      .addCase(getAccountsByPhase.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAccountsByPhase.fulfilled, (state, action) => {
        state.accounts = action.payload;
        state.isAccountSuccess = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getAccountsByPhase.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch accounts by phase";
        state.isLoading = false;
      });
  },
});

export const { clearError, clearAccount, resetAccountFlags } = accountsSlice.actions;
export default accountsSlice.reducer;